import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import ShareIcon from "@mui/icons-material/Share";
import { Box, Checkbox, ClickAwayListener, IconButton, InputAdornment, Popper, TextField, Tooltip, Typography, useMediaQuery } from "@mui/material";
import classNames from "classnames";
import { Children, cloneElement, useEffect, useState, useMemo, useRef } from "react";
import MinimizeSVG from "../../../assets/svgs/minimize.svg";
import PopupPlusSVG from "../../../assets/svgs/popup-plus.svg";
import InfoIcon from "../../icon/InfoIcon";
import HtmlTooltip from "../../shared/HtmlTooltip";
import ReportButton from "../../shared/ReportButton";
import styles from "./Card.module.scss";
import CardTooltipContent from "./CardTooltipContent";
import CommonIconButtons from "./RightAction";
import MobileCard from "./MobileCard";

const Card = ({
	children,
	flex,
	title,
	secondaryTitle = "",
	secondaryTitleError = "",
	secondaryTitleClassName = "",
	minimizable,
	itemAlign,
	isDetailsOpen = true,
	isDisabled = false,
	handleOnClickAction,
	taggedList,
	loading = false,
	isSlider = false,
	tooltipContent = undefined,
	isMinimized,
	priorityNumber = null,
	setIsMinimized = () => void 0,
	handleOnClickReport,
	handleOnClickShareReport,
	style,
	styleContent,
	canBeSearched,
	data,
	selectedItems,
	handleToggleAll,
	type,
	debug,
	pageType,
	cardType = null,
	selectAllOption = true,
	customCardData = null,
	sharedUsers = [],
	isSharedWithEveryoneFallback = false,
	handleOnClick,
	disableInteractions = false,
	isDebug = false,
	filterMenuData = null,
	selected = false,
	isExhaustCard = false
}) => {
	const isMobile = useMediaQuery("(max-width: 768px)");
	
	// Desktop hooks - must be called before conditional return
	const [showDetails, setShowDetails] = useState(false);
	const [showSearch, setShowSearch] = useState(false);
	const [searchValue, setSearchValue] = useState("");
	const [shareOpen, setShareOpen] = useState(false);
	const shareButtonRef = useRef(null);

	useEffect(() => {
		setShowDetails(isDisabled ? false : isDetailsOpen);
	}, [isDetailsOpen, isDisabled]);

	useEffect(() => {
		if (!disableInteractions) setShareOpen(false);
	}, [disableInteractions]);

	// Filter data based on search value - must be before conditional return
	const filteredData = useMemo(() => {
		if (!searchValue || !data) return data;
		const searchLower = searchValue.toLowerCase().trim();
		return data.filter((x) => x.label?.toLowerCase().includes(searchLower));
	}, [data, searchValue]);

	// If mobile, render the mobile-specific card component
	if (isMobile) {
		return (
			<MobileCard
				children={children}
				flex={flex}
				title={title}
				secondaryTitle={secondaryTitle}
				secondaryTitleError={secondaryTitleError}
				secondaryTitleClassName={secondaryTitleClassName}
				minimizable={minimizable}
				itemAlign={itemAlign}
				isDetailsOpen={isDetailsOpen}
				isDisabled={isDisabled}
				handleOnClickAction={handleOnClickAction}
				taggedList={taggedList}
				loading={loading}
				isSlider={isSlider}
				tooltipContent={tooltipContent}
				isMinimized={isMinimized}
				priorityNumber={priorityNumber}
				setIsMinimized={setIsMinimized}
				handleOnClickReport={handleOnClickReport}
				style={style}
				styleContent={styleContent}
				canBeSearched={canBeSearched}
				data={data}
				selectedItems={selectedItems}
				handleToggleAll={handleToggleAll}
				type={type}
				debug={debug}
				pageType={pageType}
				cardType={cardType}
				selectAllOption={selectAllOption}
				customCardData={customCardData}
				sharedUsers={sharedUsers}
				isSharedWithEveryoneFallback={isSharedWithEveryoneFallback}
				handleOnClick={handleOnClick}
				disableInteractions={disableInteractions}
				isDebug={isDebug}
				filterMenuData={filterMenuData}
				selected={selected}
				isExhaustCard={isExhaustCard}
			/>
		);
	}

	// Desktop version - original code
	const usePropsMinimizeStatus = isMinimized !== undefined;
	const showStatus = usePropsMinimizeStatus ? isMinimized : showDetails;

	const numberOfCheckedItems = data?.reduce((a, v) => {
		const itemId = v._id ? v._id : v.id;
		const isInSelectedItems = selectedItems.find((x) => x === itemId);
		if (isInSelectedItems) {
			return a + 1;
		}
		return a;
	}, 0);
	const allItemsChecked = numberOfCheckedItems === data?.length;

	// Check if all searched items are selected
	const numberOfCheckedSearchedItems = filteredData?.reduce((a, v) => {
		const itemId = v._id ? v._id : v.id;
		const isInSelectedItems = selectedItems.find((x) => x === itemId);
		if (isInSelectedItems) {
			return a + 1;
		}
		return a;
	}, 0);
	const allSearchedItemsChecked = filteredData?.length > 0 && numberOfCheckedSearchedItems === filteredData?.length;
	const isSharedWithMe = Boolean(customCardData?.isShared);
	const isSharedWithEveryone = Boolean(customCardData?.isUniversalAccess || customCardData?.isUniversal || isSharedWithEveryoneFallback);
	const shouldShowShareIcon = Boolean(customCardData?._id) && (isSharedWithMe || !isSharedWithEveryone);
	/** Share chip only while `disableInteractions` (layout mode on pages that still mount `Card`). Hidden on the normal dashboard — use layout preview badge there instead. */
	const shareIconVisible = shouldShowShareIcon && disableInteractions;
	const sharedByName =
		customCardData?.shareInfo?.sharedBy?.fullName ||
		customCardData?.sharedBy?.fullName ||
		customCardData?.sharedByName ||
		customCardData?.ownerName ||
		customCardData?.createdBy?.fullName ||
		null;

	const handleOpenShareInfo = (event) => {
		event.stopPropagation();
		setShareOpen((prev) => !prev);
	};

	const handleCloseShareInfo = () => {
		setShareOpen(false);
	};

	const childrenWithProps = children
		? Children.map(children, (child) =>
			cloneElement(child, {
				searchValue,
			})
		)
		: null;

	const cardClassName = classNames(
		styles.card,
		isDisabled && styles.disabled,
		selected && !loading && !isExhaustCard && styles.selected,
		isExhaustCard && styles.exhaustCard,
		isExhaustCard && selected && !loading && styles.exhaustSelected
	);

	// Merge styles with selected highlight, ensuring selected styles override style prop
	const baseStyle = {
		flex,
		height: `${showStatus || loading ? "100%" : "fit-content"}`,
		...style
	};

	// For exhaust card, don't apply inline styles - let CSS handle it
	// For regular cards, apply inline styles when selected
	const mergedStyle = selected && !loading && !isExhaustCard ? {
		...baseStyle,
		boxShadow: '0px 0px 14px #fa7226',
		border: '2px solid #fa7226'
	} : baseStyle;

	return (
		<div
			className={`${cardClassName} dashboard-card-view`}
			style={mergedStyle}
			{...handleOnClick && { onClick: handleOnClick }}
		>
			<div className={(classNames("ffmar fs15 fw700 "), styles.cardHeaderContainer)}>
				<div className={classNames("df aic ffmar fs15 fw700", styles.cardHeader)}>
					{showSearch && canBeSearched && !loading ? (
						<ClickAwayListener
							mouseEvent="onMouseDown"
							touchEvent="onTouchStart"
							onClickAway={() => {
								showSearch && setShowSearch(false);
							}}
						>
							<TextField
								placeholder="Search"
								type="search"
								variant="outlined"
								fullWidth
								size="small"
								value={searchValue}
								onChange={(e) => {
									setSearchValue(e.target.value);
								}}
								sx={{ height: "29px" }}
								InputProps={{
									startAdornment: (
										<InputAdornment position="start">
											<SearchIcon />
										</InputAdornment>
									),
									endAdornment: searchValue && filteredData?.length > 0 && selectAllOption ? (
										<InputAdornment position="end">
											<Tooltip title={allSearchedItemsChecked ? "Clear all searched" : "Select all searched"} arrow>
												<IconButton
													disableFocusRipple
													disableRipple
													size="small"
													onClick={disableInteractions ? undefined : () => {
														handleToggleAll({ items: filteredData, type, cardTitle: title });
													}}
													disabled={loading || disableInteractions}
													sx={{
														p: 0.5,
														width: 20,
														height: 20,
													}}
												>
													{allSearchedItemsChecked ? (
														<CheckCircleIcon sx={{ width: "16px", height: "16px", color: "#4879f5" }} />
													) : (
														<CheckCircleOutlineIcon sx={{ width: "16px", height: "16px", color: "#637381" }} />
													)}
												</IconButton>
											</Tooltip>
										</InputAdornment>
									) : null,
								}}
							/>
						</ClickAwayListener>
					) : (
						<>
							{canBeSearched && (
								<Tooltip title="Search" arrow>
									<IconButton
										disableFocusRipple
										disableRipple
										sx={{
											width: 22,
											height: 21,
											mr: 1.2,
											borderRadius: "50%",
											border: loading ? "none" : "2px solid #637381",
											backgroundColor: loading ? "transparent" : searchValue !== "" ? "#4879f5" : "#fff",
											color: loading ? "inherit" : "black",
										}}
										onClick={loading || disableInteractions ? undefined : () => setShowSearch(true)}
										className={loading ? "skeleton" : undefined}
										disabled={disableInteractions}
									>
										{!loading && <SearchIcon sx={{ width: "16px", height: "14px" }} />}
									</IconButton>
								</Tooltip>
							)}
							{selectAllOption && (canBeSearched && ((minimizable && isMinimized) || !minimizable)) && (
								<Tooltip title={searchValue ? (allSearchedItemsChecked ? "Clear all searched" : "Select all searched") : "Select/Clear all"} arrow>
									<Checkbox
										disableRipple
										edge="end"
										icon={<CheckCircleOutlineIcon />}
										checkedIcon={<CheckCircleIcon />}
										onChange={disableInteractions ? undefined : () => handleToggleAll({ items: searchValue ? filteredData : data, type, cardTitle: title })}
										checked={searchValue ? (allSearchedItemsChecked && !loading) : (allItemsChecked && !loading)}
										disabled={loading || disableInteractions}
										sx={{ p: 0, mr: "5px", pl: 0, ml: "-7px", borderRadius: "10000px" }}
									/>
								</Tooltip>
							)}
							{tooltipContent && (
								<div className={styles.cardTooltipIcon}>
									<HtmlTooltip content={!loading && <CardTooltipContent content={tooltipContent} />}>
										<IconButton
											disableFocusRipple={true}
											disableRipple={true}
											sx={{ cursor: loading || disableInteractions ? "default" : "pointer" }}
											disabled={disableInteractions}
										>
											<InfoIcon />
										</IconButton>
									</HtmlTooltip>
								</div>
							)}

							<Typography
								className={styles.title}
								variant="subtitle1"
								component="div"
								sx={{
									maxWidth: isMobile ? "85%" : "50%",
									fontSize: { xs: "0.8rem", md: "0.8rem" },
									minWidth: 0,
									flex: "1 1 auto",
									display: "flex",
									flexDirection: "column",
									alignItems: "stretch",
									gap: 0.25,
								}}
							>
								<Box
									sx={{
										display: "flex",
										flexDirection: "row",
										flexWrap: "nowrap",
										justifyContent: "flex-start",
										alignItems: "flex-start",
										columnGap: "14px",
										minWidth: 0,
										width: "100%",
									}}
								>
									<Box
										component="span"
										sx={{
											flex: "0 1 auto",
											minWidth: 0,
											maxWidth: shareIconVisible ? "calc(100% - 40px)" : "100%",
											overflowWrap: "anywhere",
											wordBreak: "break-word",
										}}
									>
										{title} &nbsp; &nbsp;<b>{priorityNumber && `#${priorityNumber}`}</b>
									</Box>
									{shareIconVisible && (
										<Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "flex-start", pt: "1px" }}>
											<IconButton
												ref={shareButtonRef}
												disableFocusRipple
												disableRipple
												data-no-drag="true"
												onMouseDown={(e) => e.stopPropagation()}
												onClick={handleOpenShareInfo}
												sx={{
													width: 22,
													height: 21,
													p: 0,
													borderRadius: "50%",
													border: "2px solid #4879f5",
													backgroundColor: "#fff",
													color: "#4879f5",
													transition:
														"background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease",
													"&:hover": {
														backgroundColor: "rgba(72, 121, 245, 0.12)",
														borderColor: "#3568d4",
														color: "#3568d4",
													},
												}}
											>
												<ShareIcon sx={{ width: "16px", height: "14px" }} />
											</IconButton>
											<Popper
												open={shareOpen}
												anchorEl={shareButtonRef.current}
												placement="bottom-start"
												sx={{ zIndex: 1300 }}
											>
												<ClickAwayListener onClickAway={handleCloseShareInfo}>
													<Box
														data-no-drag="true"
														onMouseDown={(e) => e.stopPropagation()}
														sx={{
															p: 1.5,
															minWidth: 220,
															maxWidth: 320,
															bgcolor: "background.paper",
															borderRadius: 1,
															boxShadow: 3,
															mt: 0.5,
														}}
													>
														<Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
															{isSharedWithMe ? "Shared by" : "Shared with"}
														</Typography>
														{isSharedWithMe ? (
															<Typography variant="body2" sx={{ mb: 0.5 }}>
																{sharedByName || "Unknown"}
															</Typography>
														) : sharedUsers.length > 0 ? (
															sharedUsers.map((userName) => (
																<Typography key={userName} variant="body2" sx={{ mb: 0.5 }}>
																	- {userName}
																</Typography>
															))
														) : (
															<Typography variant="body2" color="text.secondary">
																Not shared with anyone
															</Typography>
														)}
													</Box>
												</ClickAwayListener>
											</Popper>
										</Box>
									)}
								</Box>
								{secondaryTitle && <div className={classNames("fs10", styles.secondaryTitle)}>{secondaryTitle}</div>}
								{secondaryTitleError && (
									<div className={classNames("fs10", styles.secondaryTitle, secondaryTitleClassName)}>
										{secondaryTitleError}
									</div>
								)}
							</Typography>

							<div className={`df aic mla`}>
								{handleOnClickReport &&
									(loading ? (
										<div
											style={{
												width: "22px",
												height: "22px",
												borderRadius: "50%",
												marginRight: "7px",
											}}
											className="skeleton"
										></div>
									) : (
										<ReportButton
											customCardData={customCardData}
											cardType={cardType || type}
											handleOnClickReport={disableInteractions ? undefined : handleOnClickReport}
											handleOnClickShareReport={disableInteractions ? undefined : handleOnClickShareReport}
											title={title}
											sx={{
												width: 23,
												height: 23,
											}}
											pageType={pageType}
											openFrom="card"
											disabled={disableInteractions}
										/>
									))}
								{!isDisabled && (
									<CommonIconButtons
										title={title}
										handleOnClickAction={disableInteractions ? undefined : handleOnClickAction}
										loading={loading}
										disabled={disableInteractions}
									/>
								)}
								{minimizable && (
									<div
										className="p-l-9 p-r-5 p-t-5 p-b-5 m-l-15"
										style={{ cursor: disableInteractions ? "default" : "pointer" }}
										onClick={disableInteractions ? undefined : () =>
											usePropsMinimizeStatus ? setIsMinimized(!isMinimized) : setShowDetails(!showDetails)
										}
									>
										{showStatus ? <MinimizeSVG /> : <PopupPlusSVG />}
									</div>
								)}
							</div>
						</>
					)}
				</div>
				{taggedList && taggedList}
			</div>
			{!isSlider && showStatus && isDisabled === false && (
				<div
					className={classNames("df", styles.cardContent, itemAlign ? itemAlign : "aic")}
					{...(styleContent && { style: styleContent })}
				>
					{childrenWithProps}
				</div>
			)}
			{isSlider && showStatus && isDisabled === false && childrenWithProps}
		</div>
	);
};

export default Card;
